import React, { useState, useEffect } from 'react';
import { X, Save, User, Phone, Users, Baby } from 'lucide-react';
import { RSVPResponse } from '../types/rsvp';

interface EditRSVPModalProps {
  response: RSVPResponse;
  isOpen: boolean;
  onClose: () => void;
  onSave: (updatedResponse: RSVPResponse) => void;
}

const EditRSVPModal: React.FC<EditRSVPModalProps> = ({ response, isOpen, onClose, onSave }) => {
  const [formData, setFormData] = useState<RSVPResponse>(response);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setFormData(response);
  }, [response]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      await onSave(formData);
      onClose();
    } catch (error) {
      // Handle error silently
      alert('Failed to save changes. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field: keyof RSVPResponse, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleGuestCountChange = (newCount: number) => {
    const currentAdults = formData.adultCount || 0;
    const currentChildren = formData.childCount || 0;
    const currentTotal = currentAdults + currentChildren;

    let newAdults = currentAdults;
    let newChildren = currentChildren;

    if (newCount > currentTotal) {
      // Add to adults by default
      newAdults = currentAdults + (newCount - currentTotal);
    } else if (newCount < currentTotal) {
      // Remove from children first, then adults
      const childrenToRemove = Math.min(currentChildren, currentTotal - newCount);
      const adultsToRemove = (currentTotal - newCount) - childrenToRemove;

      newChildren = currentChildren - childrenToRemove;
      newAdults = currentAdults - adultsToRemove;
    }

    // Update all values in a single state update to avoid race conditions
    setFormData(prev => ({
      ...prev,
      guestCount: newCount,
      adultCount: newAdults,
      childCount: newChildren
    }));
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">Edit RSVP Response</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Guest Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <User className="w-4 h-4 inline mr-2" />
              Guest Name
            </label>
            <input
              type="text"
              value={formData.guestName}
              onChange={(e) => handleInputChange('guestName', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          {/* Phone */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Phone className="w-4 h-4 inline mr-2" />
              Phone Number
            </label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => handleInputChange('phone', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>



          {/* Wedding Code */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Wedding Code
            </label>
            <input
              type="text"
              value={formData.weddingCode}
              onChange={(e) => handleInputChange('weddingCode', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          {/* Attending Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Attendance Status
            </label>
            <div className="flex space-x-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="attending"
                  checked={formData.attending}
                  onChange={() => handleInputChange('attending', true)}
                  className="mr-2"
                />
                Yes, I'll be attending
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="attending"
                  checked={!formData.attending}
                  onChange={() => handleInputChange('attending', false)}
                  className="mr-2"
                />
                No, I can't attend
              </label>
            </div>
          </div>

          {/* Guest Count Summary - Always Visible */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-blue-900 mb-3 flex items-center">
              <Users className="w-5 h-5 mr-2" />
              Guest Count Summary
            </h3>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div className="bg-white rounded-lg p-3 border border-blue-100">
                <div className="text-2xl font-bold text-blue-600">{formData.guestCount || 0}</div>
                <div className="text-sm text-gray-600">Total Guests</div>
              </div>
              <div className="bg-white rounded-lg p-3 border border-blue-100">
                <div className="text-2xl font-bold text-blue-600">{formData.adultCount || 0}</div>
                <div className="text-sm text-gray-600">Adults</div>
              </div>
              <div className="bg-white rounded-lg p-3 border border-blue-100">
                <div className="text-2xl font-bold text-blue-600">{formData.childCount || 0}</div>
                <div className="text-sm text-gray-600">Children</div>
              </div>
            </div>
          </div>

          {/* Guest Count Controls - Always Visible */}
          <div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Users className="w-4 h-4 inline mr-2" />
                  Adults
                </label>
                <div className="flex items-center">
                  <button
                    type="button"
                    onClick={() => {
                      const newAdults = Math.max(0, (formData.adultCount || 0) - 1);
                      const children = formData.childCount || 0;
                      const newTotal = newAdults + children;
                      setFormData(prev => ({
                        ...prev,
                        adultCount: newAdults,
                        guestCount: Math.max(0, newTotal)
                      }));
                    }}
                    className="w-10 h-10 rounded-l-lg bg-blue-100 hover:bg-blue-200 flex items-center justify-center text-blue-700 border border-blue-200"
                  >
                    -
                  </button>
                  <div className="h-10 px-4 flex items-center justify-center bg-white border-t border-b border-blue-200 min-w-[60px] text-center">
                    {formData.adultCount || 0}
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      const newAdults = Math.min(10, (formData.adultCount || 0) + 1);
                      const children = formData.childCount || 0;
                      const newTotal = newAdults + children;
                      setFormData(prev => ({
                        ...prev,
                        adultCount: newAdults,
                        guestCount: newTotal
                      }));
                    }}
                    className="w-10 h-10 rounded-r-lg bg-blue-100 hover:bg-blue-200 flex items-center justify-center text-blue-700 border border-blue-200"
                  >
                    +
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Baby className="w-4 h-4 inline mr-2" />
                  Children
                </label>
                <div className="flex items-center">
                  <button
                    type="button"
                    onClick={() => {
                      const newChildren = Math.max(0, (formData.childCount || 0) - 1);
                      const adults = formData.adultCount || 0;
                      const newTotal = adults + newChildren;
                      setFormData(prev => ({
                        ...prev,
                        childCount: newChildren,
                        guestCount: Math.max(0, newTotal)
                      }));
                    }}
                    className="w-10 h-10 rounded-l-lg bg-green-100 hover:bg-green-200 flex items-center justify-center text-green-700 border border-green-200"
                  >
                    -
                  </button>
                  <div className="h-10 px-4 flex items-center justify-center bg-white border-t border-b border-green-200 min-w-[60px] text-center">
                    {formData.childCount || 0}
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      const newChildren = Math.min(10, (formData.childCount || 0) + 1);
                      const adults = formData.adultCount || 0;
                      const newTotal = adults + newChildren;
                      setFormData(prev => ({
                        ...prev,
                        childCount: newChildren,
                        guestCount: newTotal
                      }));
                    }}
                    className="w-10 h-10 rounded-r-lg bg-green-100 hover:bg-green-200 flex items-center justify-center text-green-700 border border-green-200"
                  >
                    +
                  </button>
                </div>
              </div>
            </div>
          </div>



          {/* Action Buttons */}
          <div className="flex justify-end space-x-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 transition-colors"
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              <Save className="w-4 h-4 mr-2" />
              {isLoading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditRSVPModal;
